import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type {
    PipelineHistoryParams,
    PipelineHistoryResponse,
    PipelineListParams,
    PipelineListResponse,
    PipelineLogsParams,
    PipelineLogsResponse,
} from '../services/pipeline/pipeline.types';
import { pipelineService } from '../services/pipeline/pipeline.service';

type UsePipelineListOptions = Omit<
    UseQueryOptions<
        PipelineListResponse, // TQueryFnData
        Error,                // TError
        PipelineListResponse, // TData (no select override here, but can extend later)
        ['pipeline-list', Partial<PipelineListParams>] // TQueryKey
    >,
    'queryKey' | 'queryFn'
>;

export const usePipelineListQuery = (
    params: PipelineListParams = {},
    options?: UsePipelineListOptions
) => {
    return useQuery({
        queryKey: ['pipeline-list', params],
        queryFn: () => pipelineService.fetchPipelineList(params),
        refetchOnMount: 'always',
        placeholderData: (previousData) => previousData,
        staleTime: 30_000, // 30 seconds
        ...options,
    });
};

type UsePipelineHistoryOptions = Omit<
    UseQueryOptions<
        PipelineHistoryResponse,
        Error,
        PipelineHistoryResponse,
        ['pipeline-history', PipelineHistoryParams]
    >,
    'queryKey' | 'queryFn'
>;

export const usePipelineHistoryQuery = (
    params: PipelineHistoryParams,
    options?: UsePipelineHistoryOptions
) => {
    return useQuery({
        queryKey: ['pipeline-history', params],
        queryFn: () => pipelineService.fetchPipelineHistory(params),
        enabled: Boolean(params.data_flow_id),
        refetchOnMount: 'always',
        // Intentionally NO placeholderData: when the modal reopens for a different
        // pipeline, we want the table to render its loading state, not the
        // observer's last-seen rows from the previous pipeline.
        staleTime: 0,
        gcTime: 0,
        ...options,
    });
};

type UsePipelineLogsOptions = Omit<
    UseQueryOptions<
        PipelineLogsResponse,
        Error,
        PipelineLogsResponse,
        ['pipeline-logs', PipelineLogsParams]
    >,
    'queryKey' | 'queryFn'
>;

export const usePipelineLogsQuery = (
    params: PipelineLogsParams,
    options?: UsePipelineLogsOptions
) => {
    return useQuery({
        queryKey: ['pipeline-logs', params],
        queryFn: () => pipelineService.fetchPipelineLogs(params),
        enabled: Boolean(params.data_flow_id && params.run_id),
        refetchOnMount: 'always',
        staleTime: 0,
        ...options,
    });
};